import { api } from './api';

/* Mirrors Eduvora.Application.Presence.
   ⚠ NOT A SINGLE MONEY FIELD IN HERE, deliberately — a class teacher can reach this endpoint. */

export interface StaffAway {
  employeeId: string;
  fullName: string;
  designation: string | null;
  department: string | null;
  /** ⚠ null on the staffroom view. Only the HR endpoint fills these two in — a leave reason is
      routinely health information. See PresenceEndpoints for the split. */
  leaveType: string | null;
  reason: string | null;
  fromDate: string;
  toDate: string;
  days: number;
  isHalfDay: boolean;
  pending: boolean;
  startsInDays: number;
  daysRemaining: number;
  /** Sections this person is class teacher for. Non-empty means a class is uncovered. */
  classesUncovered: string[];
}

export interface StudentAway {
  studentId: string;
  fullName: string;
  admissionNumber: string;
  classKey: string;
  classLabel: string;
  /** "register" = the roll says so (fact). "application" = a leave request covers the date
      (intent, and the only thing that exists for a future date). */
  source: 'register' | 'application';
  status: string;
  reason: string | null;
  fromDate: string | null;
  toDate: string | null;
}

export interface ClassPresence {
  classKey: string;
  classLabel: string;
  strength: number;
  present: number;
  absent: number;
  late: number;
  onLeave: number;
  unmarked: number;
  /** ⚠ null means nothing was marked — NOT 0%. Render "—". */
  presentPct: number | null;
}

export interface DayPresence {
  date: string;
  isFuture: boolean;
  isToday: boolean;
  isNonWorkingDay: boolean;
  nonWorkingReason: string | null;
  /** ⚠ THE FLAG THE WHOLE SCREEN KEYS OFF. False means no register exists for this date, so the
      present / absent / late counts are all zero because nothing was recorded — not because
      nobody came. Never render attendance figures without checking this first. */
  registerTaken: boolean;

  staffTotal: number;
  staffAwayToday: number;
  staffDaysLost: number;
  staffAwayList: StaffAway[];
  staffUpcoming: StaffAway[];
  staffPendingCount: number;

  studentsTotal: number;
  studentsOnLeave: number;
  studentsAbsent: number;
  studentsLate: number;
  studentsPresent: number;
  studentsUnmarked: number;
  studentPresentPct: number | null;
  studentsAwayList: StudentAway[];
  classes: ClassPresence[];

  note: string | null;
}

export interface PresenceQuery {
  date?: string | null;
  institutionId?: string | null;
  upcoming?: number | null;
  /** Ask the HR route, which adds leave type and reason. Only send true for a role that is
      actually in the `hr` policy, or the request comes back 403. */
  withReasons?: boolean;
}

export const presenceApi = {
  day: (q: PresenceQuery = {}) => {
    const p = new URLSearchParams();
    if (q.date) p.set('date', q.date);
    if (q.institutionId) p.set('institutionId', q.institutionId);
    if (q.upcoming) p.set('upcoming', String(q.upcoming));
    const qs = p.toString();
    const path = q.withReasons ? '/api/presence/day/hr' : '/api/presence/day';
    return api.get<DayPresence>(`${path}${qs ? '?' + qs : ''}`);
  },
};

/** yyyy-mm-dd in LOCAL time.
 *  ⚠ NOT toISOString().slice(0,10) — that converts to UTC first, so anywhere east of Greenwich
 *  (which includes every school this product will ever be sold to) an evening click on the 9th
 *  sends the 8th, and "who is on leave today" quietly answers about yesterday. */
export function isoDate(d: Date): string {
  const m = `${d.getMonth() + 1}`.padStart(2, '0');
  const day = `${d.getDate()}`.padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

/** "Today", "Tomorrow", "Mon 17 Aug" — a principal reads a weekday faster than a date. */
export function dayLabel(iso: string): string {
  const d = new Date(iso + 'T00:00:00');
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diff = Math.round((d.getTime() - today.getTime()) / 86_400_000);
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Tomorrow';
  if (diff === -1) return 'Yesterday';
  return d.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' });
}
